import { useEffect, useState } from "react";
import { db } from "../firebase/firebase";
import { collection, onSnapshot } from "firebase/firestore";

import { doc, runTransaction } from "firebase/firestore"; // add to existing import line

// ---------------------------------------------------------------------
// Project numbering — one Client ID can now have multiple projects
// (multiple `clients` docs sharing the same clientId, via Clients.js's
// "Add Project"). Each project gets a permanent, 1-based `projectNumber`,
// assigned ONCE at creation time via an atomic transaction against a
// per-clientId counter doc (`counters/projectSeq_<clientId>`) — same
// collision-safe pattern already used for `counters/mainInvoiceNumber`.
// ---------------------------------------------------------------------

const sanitizeForDocId = (str) =>
  (str || "").trim().replace(/[^a-zA-Z0-9_-]/g, "_") || "unknown";

/**
 * Atomically assigns the next projectNumber for a clientId. Call this
 * ONCE, right before creating a new `clients` document (see Clients.js),
 * and store the returned number on that document as `projectNumber`.
 */
export async function assignProjectNumber(clientId) {
  const counterRef = doc(db, "counters", `projectSeq_${sanitizeForDocId(clientId)}`);
  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const last = snap.exists() ? Number(snap.data().lastSequence) || 0 : 0;
    const next = last + 1;
    transaction.set(
      counterRef,
      { lastSequence: next, updatedAt: new Date().toISOString() },
      { merge: true },
    );
    return next;
  });
}

/** "01", "02" ... "10", "100" — min 2 digits, never truncated above 99. */
export function formatProjectSeq(num) {
  const n = Number(num);
  if (Number.isNaN(n) || n <= 0) return "01";
  return String(n).padStart(2, "0");
}

/**
 * Resolves a project's 1-based number. Prefers the permanent
 * `projectNumber` field (set at creation — see assignProjectNumber).
 * Falls back to rank-by-creation-order among clientId siblings for
 * legacy project docs created before this field existed — this always
 * agrees with getProjectRef()'s existing ordering below.
 */
export function resolveProjectNumber(clientsById, clientDocId) {
  const target = clientsById?.[clientDocId];
  if (!target) return 1;
  if (target.projectNumber) return Number(target.projectNumber);
  if (!target.clientId) return 1;

  const targetKey = target.clientId.trim().toLowerCase();
  const siblings = Object.values(clientsById).filter(
    (c) => (c.clientId || "").trim().toLowerCase() === targetKey,
  );
  siblings.sort((a, b) => {
    const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return aTime - bTime;
  });
  const idx = siblings.findIndex((c) => c.id === clientDocId);
  return idx === -1 ? 1 : idx + 1;
}



export const PROJECT_REF_BADGE_STYLE = {
  display: "inline-block",
  marginLeft: 6,
  padding: "1px 7px",
  fontSize: 11,
  fontWeight: 700,
  borderRadius: 999,
  background: "#fff4e0",
  color: "#a8690f",
  border: "1px solid #f0d29b",
  verticalAlign: "middle",
};

/**
 * Realtime map of clientDocId -> full client record (name, status, etc),
 * kept live via a Firestore onSnapshot subscription on the `clients`
 * collection.
 *
 * This is the SINGLE SOURCE OF TRUTH for client/project status. Estimation,
 * Invoice, and Materials pages should never store or display their own
 * copy of "status" — they should always read it from this map (looked up
 * by `clientDocId`) so that a status change made on any page is reflected
 * everywhere else instantly, without a manual refetch.
 *
 * Writing a status change should ALWAYS be done via:
 *   updateDoc(doc(db, "clients", clientDocId), { status: "..." })
 * Every subscribed page picks the change up automatically.
 */
export function useClientsStatusMap() {
  const [clientsById, setClientsById] = useState({});
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "clients"),
      (snapshot) => {
        const map = {};
        snapshot.forEach((docSnap) => {
          map[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        setClientsById(map);
        setClientsLoading(false);
      },
      (err) => {
        console.error("Failed to subscribe to clients collection:", err);
        setClientsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  return { clientsById, clientsLoading };
}

/**
 * Helper for resolving the status to display for a record that references
 * a client via `clientDocId`. Falls back to a locally-stored status field
 * (legacy data, or while the realtime map is still loading) so nothing
 * ever renders blank.
 */
export function resolveClientStatus(clientsById, clientDocId, fallbackStatus) {
  if (clientDocId && clientsById[clientDocId]?.status) {
    return clientsById[clientDocId].status;
  }
  return fallbackStatus || "-";
}
/**
 * Derives a stable "P-01", "P-02"... project reference for a given
 * clientDocId, based purely on existing data: every project already
 * lives as its own `clients` document sharing the same `clientId`. This
 * requires no new Firestore field and no migration — a client's project
 * reference is simply its rank (by creation order) among every document
 * sharing that same Client ID.
 *
 * Returns null when the client has only ONE project under its Client ID
 * (the common case), so callers can skip rendering the badge entirely
 * for existing single-project clients — exactly matching the
 * "single-project clients continue working normally" requirement.
 */
export function getProjectRef(clientsById, clientDocId) {
  const target = clientsById?.[clientDocId];
  if (!target || !target.clientId) return null;

  const targetKey = target.clientId.trim().toLowerCase();
  const siblings = Object.values(clientsById).filter(
    (c) => (c.clientId || "").trim().toLowerCase() === targetKey,
  );

  if (siblings.length <= 1) return null;

  siblings.sort((a, b) => {
    const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return aTime - bTime;
  });

  const idx = siblings.findIndex((c) => c.id === clientDocId);
  return idx === -1 ? null : `P-${String(idx + 1).padStart(2, "0")}`;
}